/** Устройство до и после проверки: по смене отметки доставки видно, отправил ли сервер. */
export interface DeviceDelivery {
  name: string;
  /** Отметка последней успешной отправки до проверки. */
  before: string | null;
  /**
   * Отметка после проверки. `undefined` — устройства в списке уже нет: сервер
   * убирает подписку, когда push-служба отвечает, что она отозвана.
   */
  after: string | null | undefined;
}

export interface DeliveryReport {
  confirmed: string[];
  revoked: string[];
  missing: string[];
  message: string;
}

const join = (names: string[]) => names.join(", ");

/**
 * Итог проверки по устройствам простыми словами.
 *
 * Отметку ставит сервер, когда push-служба (Apple, Google, Microsoft) приняла
 * сообщение. Сравнение «до и после» не зависит от часов устройства и сервера.
 * «Приняла» не значит «показала»: если доставка подтверждена, а окна нет, дело
 * в настройках самого устройства; если не подтверждена нигде — на сервере.
 * Так проверка отделяет две разные поломки, которые снаружи выглядят одинаково.
 */
export function deliveryReport(devices: DeviceDelivery[]): DeliveryReport {
  const confirmed: string[] = [];
  const revoked: string[] = [];
  const missing: string[] = [];
  for (const device of devices) {
    if (device.after === undefined) revoked.push(device.name);
    else if (device.after !== null && device.after !== device.before) confirmed.push(device.name);
    else missing.push(device.name);
  }

  const parts: string[] = [];
  if (confirmed.length > 0) {
    parts.push(
      `Push-служба приняла уведомление для: ${join(confirmed)}. Если окна на этом устройстве нет, дело в его настройках — откройте подсказку «Не видно уведомлений?».`,
    );
  }
  if (revoked.length > 0) {
    parts.push(
      `Убрано сервером как отозванное: ${join(revoked)}. Это устаревшие подписки, например после переустановки приложения; на таком устройстве нажмите «Подключить это устройство» заново.`,
    );
  }
  if (missing.length > 0) {
    parts.push(
      confirmed.length === 0 && revoked.length === 0
        ? `За время наблюдения ни одно устройство не подтвердило доставку: ${join(missing)}. Значит, уведомления не уходят с сервера: проверьте, что работают воркер очереди уведомлений (queue-fast) и планировщик (scheduler), а в журнале воркера нет записей «web push delivery failed».`
        : `Доставка не подтверждена для: ${join(missing)}. Отключите устройство и подключите заново.`,
    );
  }

  return { confirmed, revoked, missing, message: parts.join(" ") };
}
